<template>
  <VModal
    :showing="showing"
    container-classes="max-w-xl"
    @update:showing="isDeckEditTypeSelected ? cancel() : close()"
    @esc="isDeckEditTypeSelected ? cancel() : close()"
  >
    <template #header>
      <h2 class="m-0 text-xl font-medium text-gray-900 capitalize leading-6 line-clamp-2">Secure Links</h2>
      <p class="mt-2 mb-0 text-sm text-gray-700">Share Secure or Private Decks with people outside your company.</p>
    </template>

    <template #default>
      <div class="overflow-y-auto text-sm text-gray-500" style="max-height: 480px">
        <EditorSecureAccessEmptyState v-if="!state.records.length" label="secure link" action="create" />
        <div
          v-for="(record, index) in state.records"
          v-else
          :key="record.id"
          class="flex items-start gap-4"
          :class="{ 'border-t border-gray-100 pt-4': index > 0 }"
        >
          <div class="w-12">
            <div class="relative flex items-center justify-center w-8 h-8 bg-black rounded-full">
              <LinkIcon class="w-4 h-4 text-white" />
              <LockClosedIcon v-if="record.has_password" class="absolute w-4 h-4 text-gold-50 -bottom-1 -right-1" />
            </div>
          </div>
          <div class="flex-1">
            <p class="m-0 text-base text-gray-900">{{ record.name }}</p>
            <p class="m-0 text-sm text-gray-700">
              <span>Created {{ formatDateMed(record.created_at) }}</span
              ><span v-if="record.has_password">&nbsp;&nbsp;&bullet;&nbsp;&nbsp;Password Protected</span>
            </p>
            <p class="max-w-md m-0 text-xs text-gray-700 leading-5">
              <span v-for="(email, emailIndex) in record.email_addresses" :key="email">
                <span v-if="emailIndex > 0">,</span><span>{{ ` ${email}` }}</span>
              </span>
            </p>
            <p
              v-if="!record.copied"
              class="my-4 text-sm cursor-pointer font-regular text-copper-700 transition ease-in-out duration-150 hover_text-copper-900 focus_outline-none focus_underline"
              @click="copyLink(record)"
            >
              Copy Link
            </p>
            <p v-else class="my-4 text-sm font-regular">Copied!</p>
          </div>
          <div class="m-1 w-24 text-right">
            <VButton variant="light" size="sm" @click="destroy(record)">
              <TrashIcon class="w-4 h-4 text-gray-700" />
            </VButton>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <VButton v-if="isDeckEditTypeSelected" @click="cancel">Back</VButton>
        <VButton v-else @click="$emit('close')">Cancel</VButton>
        <VButton variant="copper" @click="$emit('goto:create-link')">New Secure Link</VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { reactive, toRef, watch, computed } from 'vue'
import { LockClosedIcon } from '@heroicons/vue/24/solid'
import { LinkIcon, TrashIcon } from '@heroicons/vue/24/outline'

import type { EditDeck } from '~/composables/useEditDeck'
import { formatDateMed } from '@/lib/datetime-format-utils'

interface SecureLink {
  id: number
  uuid: string
  name: string
  email_addresses: string[]
  notify_recipients: boolean
  deck_id: number
  created_at: string
  has_password: boolean
  copied: boolean
}

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['link-destroyed', 'goto:secure-users', 'goto:create-link', 'error', 'close'])

const state = reactive({
  error: null,
  records: [] as SecureLink[],
})

const { $sentry } = useNuxtApp()

const { copyToClipboard } = useCopyToClipboard()

watch(toRef(props, 'showing'), refresh, { immediate: true })

const isDeckEditTypeSelected = computed(() => props.deck?.edit_access_type === 'selected')

function cancel(): void {
  emit('goto:secure-users', false)
}

function close(): void {
  emit('close')
}

function copyLink(secureLink: SecureLink): void {
  copyToClipboard({ copyText: `${window.location.origin}/decks/${props.deck.id}?auth=${secureLink.uuid}` })
  secureLink.copied = true
}

async function destroy(secureLink: SecureLink): Promise<void> {
  try {
    await $fetch<any>(`/v2/decks/${props.deck.id}/uuids/${secureLink.id}`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
    })
    emit('link-destroyed')
    await refresh()
  } catch (e: any) {
    const error = 'response' in e ? e.response.data[0] : e.message
    emit('error', error)
    $sentry?.captureException(error)
  }
}

async function refresh(): Promise<void> {
  state.records = (await $fetch<any>(`/v2/decks/${props.deck.id}/uuids/`, { ...useFetchSharedOptions() })).results.map((x: SecureLink) => ({
    ...x,
    copied: false,
  }))
}
</script>
